import React, { useState, useCallback, useEffect } from 'react';
import ReactFlow, { 
  MiniMap, 
  Controls, 
  Background, 
  ReactFlowProvider,
  applyNodeChanges,
  applyEdgeChanges
} from 'reactflow';
import 'reactflow/dist/style.css';
import { themeStyles, customStyles } from './ArtemisStyles';
import { nodeTypes } from './ArtemisCustomNodes';

// Linear workflow nodes - left to right flow
const getInitialNodes = () => {
  const savedPositions = localStorage.getItem('artemisWorkflowPositions');
  if (savedPositions) {
    return JSON.parse(savedPositions);
  }
  
  return [
    // Step 1: Problem Definition
    {
      id: 'problem',
      data: { label: '🎯 "Optimize my code for performance"' },
      position: { x: 50, y: 300 },
      style: themeStyles.problemNode,
    },
    
    // Step 2: Code Input
    {
      id: 'code-input',
      data: { label: '📤 Code Upload\n(Git/Direct/API)' },
      position: { x: 400, y: 300 },
      style: themeStyles.userNode,
    },
    
    // Step 3: Analysis
    {
      id: 'analysis',
      data: { label: '🔍 Intelligent Analysis\n• AI Detection\n• Profiling\n• User Selection' },
      position: { x: 650, y: 300 },
      style: { ...themeStyles.analysisNode, width: 220 },
    },
    
    // Step 4: Code Snippets
    {
      id: 'snippets',
      data: { label: '✂️ Code Snippets\nIdentified Issues' },
      position: { x: 950, y: 300 },
      style: themeStyles.analysisNode,
    },
    
    // Step 5: AI Generation
    {
      id: 'ai-generation',
      data: { label: '🤖 Multi-Agent Generation\n• Multiple LLMs\n• Specialized Agents\n• Context Aware' },
      position: { x: 1250, y: 300 },
      style: { ...themeStyles.llmNode, width: 200 },
    },
    
    // Step 6: Evolution - UNIQUE
    {
      id: 'evolution',
      data: { label: '🧬 Evolutionary Optimization\n(Our Secret Sauce)' },
      position: { x: 1550, y: 300 },
      style: { ...themeStyles.uniqueFeatureNode, width: 250 },
    },
    
    // Step 7: Scoring
    {
      id: 'scoring',
      data: { label: '📊 Multi-Dimensional Scoring\n• AI Evaluation\n• System Metrics\n• Human Feedback' },
      position: { x: 1250, y: 500 },
      style: { ...themeStyles.metricNode, width: 220 },
    },
    
    // Step 8: Validation
    {
      id: 'validation',
      data: { label: '✅ Comprehensive Validation\n• Compilation\n• Tests\n• Benchmarks' },
      position: { x: 950, y: 500 },
      style: { ...themeStyles.analysisNode, width: 200 },
    },
    
    // Step 9: Repository Impact
    {
      id: 'repo-optimization',
      data: { label: '🗂️ Repository-Wide\nOptimization' },
      position: { x: 650, y: 500 },
      style: { ...themeStyles.uniqueFeatureNode, width: 180 },
    },
    
    // Step 10: Output
    {
      id: 'output',
      data: { label: '🔀 Automated\nPull Request' },
      position: { x: 350, y: 500 },
      style: { ...themeStyles.uniqueFeatureNode, width: 200 },
    }
  ];
};

const workflowEdges = [
  { id: 'e1', source: 'problem', target: 'code-input', animated: true, style: themeStyles.edgeStyle },
  { id: 'e2', source: 'code-input', target: 'analysis', animated: true, style: themeStyles.edgeStyle },
  { id: 'e3', source: 'analysis', target: 'snippets', style: themeStyles.edgeStyle },
  { id: 'e4', source: 'snippets', target: 'ai-generation', animated: true, style: themeStyles.edgeStyle },
  { id: 'e5', source: 'ai-generation', target: 'evolution', animated: true, style: themeStyles.evolutionEdgeStyle },
  { id: 'e6', source: 'evolution', target: 'scoring', style: themeStyles.edgeStyle },
  { id: 'e7', source: 'scoring', target: 'validation', style: themeStyles.edgeStyle },
  { id: 'e8', source: 'validation', target: 'repo-optimization', animated: true, style: themeStyles.edgeStyle },
  { id: 'e9', source: 'repo-optimization', target: 'output', animated: true, style: themeStyles.evolutionEdgeStyle },
];

const ArtemisCompleteWorkflow = () => {
  const [nodes, setNodes] = useState(getInitialNodes());
  const [edges, setEdges] = useState(workflowEdges);

  // Add custom styles on mount
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.innerText = customStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Save positions whenever nodes change
  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => {
      const updatedNodes = applyNodeChanges(changes, nds);
      // Save positions to localStorage
      const positions = updatedNodes.map(node => ({
        ...node,
        style: node.style
      }));
      localStorage.setItem('artemisWorkflowPositions', JSON.stringify(positions));
      return updatedNodes;
    });
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges((eds) => applyEdgeChanges(changes, eds));
  }, []);


  return (
    <ReactFlowProvider>
      <div style={{ 
        height: '100vh', 
        width: '100%', 
        background: 'linear-gradient(135deg, #F8F9FA 0%, #E9ECEF 100%)',
        position: 'relative'
      }}>
        <h2 style={{ 
          textAlign: 'center', 
          color: '#212529', 
          padding: '20px', 
          margin: '0',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          fontSize: '26px',
          fontWeight: '700'
        }}>
          🚀 Artemis Complete Workflow - High Level Overview
        </h2>
        
        <div style={{
          position: 'absolute',
          top: '70px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '8px 20px',
          background: 'rgba(33, 37, 41, 0.08)',
          borderRadius: '20px',
          color: '#495057',
          fontSize: '14px',
          fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
          zIndex: 100
        }}>
          From problem definition to automated pull request - the complete optimization journey
        </div>

        
        <div style={{ 
          position: 'absolute',
          top: '120px',
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 120px)'
        }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{
              padding: 0.2,
              minZoom: 0.3,
              maxZoom: 1.2
            }}
            minZoom={0.2}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 0.6 }}
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
            panOnDrag={true}
            panOnScroll={true}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <MiniMap 
              style={{
                height: 120,
                width: 180,
                backgroundColor: 'rgba(255,255,255,0.8)',
                border: '1px solid #DEE2E6'
              }}
              nodeColor={(node) => {
                return node.style?.background || '#6C757D';
              }}
              nodeStrokeColor={() => '#495057'}
              nodeBorderRadius={2}
            />
            <Controls />
            <Background 
              variant="dots" 
              color="#DEE2E6" 
              gap={20} 
              size={1} 
            />
          </ReactFlow>
        </div>

        {/* Key Features */}
        <div style={{
          position: 'absolute',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '20px',
          background: 'rgba(255,255,255,0.9)',
          padding: '15px 25px',
          borderRadius: '30px',
          border: '1px solid #DEE2E6',
          backdropFilter: 'blur(10px)'
        }}>
          <div style={{ color: '#495057', fontSize: '13px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
            <span style={{ color: '#F4511E', fontWeight: '600' }}>🧬 Evolutionary AI</span> • 
            <span style={{ marginLeft: '10px' }}>Self-Improving</span>
          </div>
          <div style={{ color: '#495057', fontSize: '13px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
            <span style={{ color: '#5E60CE', fontWeight: '600' }}>🤖 Multi-Agent</span> • 
            <span style={{ marginLeft: '10px' }}>Multiple LLMs</span>
          </div>
          <div style={{ color: '#495057', fontSize: '13px', fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif' }}>
            <span style={{ color: '#52B69A', fontWeight: '600' }}>📊 Multi-Objective</span> • 
            <span style={{ marginLeft: '10px' }}>Balanced Optimization</span>
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default ArtemisCompleteWorkflow; 